/**
 * Builds a reporting forest (one tree per top-level person) from a flat list of
 * employees linked by `manager_id`. Pure and side-effect free so it can be unit
 * tested without rendering — the org-chart overlay consumes the result.
 */

/** Minimal person shape the org chart needs (a subset of EmployeeListItem). */
export interface OrgChartPerson {
    id: number
    manager_id: number | null
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
    company_slugs: string
    /** Comma-separated team names — drives the team highlight filter. */
    team_names: string | null
}

export interface OrgTreeNode {
    person: OrgChartPerson
    children: OrgTreeNode[]
    /** Number of direct reports — drives the "· N reports" sub-label. */
    directReports: number
}

/**
 * Group people into reporting trees. Roots are people with no manager, or whose
 * manager is absent from the set (e.g. an inactive manager), or who manage
 * themselves. A visited set guards against cyclic `manager_id` chains so a bad
 * loop can never recurse forever — each person appears at most once. Anyone left
 * unreachable (e.g. a pure cycle with no entry point) is promoted to a root so
 * nobody silently disappears from the chart.
 */
/** Total people in a subtree (the node itself plus all descendants). */
export const treeSize = (node: OrgTreeNode): number =>
    1 + node.children.reduce((sum, child) => sum + treeSize(child), 0)

export const buildOrgForest = (people: OrgChartPerson[]): OrgTreeNode[] => {
    const byId = new Map<number, OrgChartPerson>()
    for (const p of people) byId.set(p.id, p)

    const childrenOf = new Map<number, OrgChartPerson[]>()
    for (const p of people) {
        if (p.manager_id === null || p.manager_id === p.id || !byId.has(p.manager_id)) continue
        const list = childrenOf.get(p.manager_id) ?? []
        list.push(p)
        childrenOf.set(p.manager_id, list)
    }

    const visited = new Set<number>()

    const build = (person: OrgChartPerson): OrgTreeNode => {
        visited.add(person.id)
        const children = (childrenOf.get(person.id) ?? [])
            .filter((c) => !visited.has(c.id))
            .map(build)
        return { person, children, directReports: children.length }
    }

    const isRoot = (p: OrgChartPerson): boolean =>
        p.manager_id === null || p.manager_id === p.id || !byId.has(p.manager_id)

    const forest = people.filter(isRoot).map(build)

    // Promote anyone left unreachable (e.g. a manager cycle) to a root.
    for (const p of people) {
        if (!visited.has(p.id)) forest.push(build(p))
    }

    return forest
}
